<script>
	// NPM Modules
	import dayjs from 'dayjs';
	import { afterUpdate, onDestroy, onMount } from 'svelte';
	import utc from 'dayjs/plugin/utc';
	// Stores
	import SportEventStore from '@/Stores/Sport/SportEventStore.js';
	import MarketGroupStore from '@/Stores/Sport/MarketGroupStore.js';
	import UrlStore from '@/Stores/UrlStore.js';
	import SportcastFixtureStore from '@/Stores/Sport/SportcastFixtureStore.js';
	import MarketStore from '@/Stores/Sport/MarketStore.js';
	import ClassificationStore from '@/Stores/Racing/ClassificationStore.js';
	import ACLStore from '@/Stores/ACLStore.js';
	// Components
	import Helmet from '@/Components/Helmet.svelte';
	import Loading from '@/Components/Loading.svelte';
	import LoadingButton from '@/Components/LoadingButton.svelte';
	import Polling from '@/Components/Polling.svelte';
	import Pagination from '@/Components/Pagination.svelte';
	// Partials
	import CreateSportEvent from '@/Partials/Sport/Manage/Events/_Create.svelte';
	import LeftMenu from '@/Partials/Sport/Dashboard/LeftMenu.svelte';
	import EventWithKeyMarketsDashboard from '@/Partials/Sport/Event/EventWithKeyMarketsDashboard.svelte';
	import ManualResult from '@/Partials/Sport/Markets/ManualResult/_ManualResult.svelte';
	// Constants
	import { EventStatus } from '@/Constants/Event.js';
	import { BetCategory } from '@/Constants/Bet.js';
	// Services
	import Sockets from '@/Services/Sockets.js';
	import { arrayEquals, paginationBuilder } from '@/Services/Helpers.js';

	dayjs.extend(utc);
	MarketGroupStore.resetStore();

	let loading = false;

	let eventStatusFilter = [EventStatus.Open, EventStatus.InPlay];
	let showStatusFilters = ACLStore.isSuper();
	let showManualResult = false;
	let resultMarket;
	let resultEvent;

	$: sportId = $UrlStore && $UrlStore.params.get('sport');
	$: compId = $UrlStore && $UrlStore.params.get('comp');
	$: (sportId || !sportId) && (compId || !compId) && getSportEvents(true);
	$: sport = sportId && $SportEventStore && $SportEventStore.firstWhere('id', sportId);
	$: events = $SportEventStore && eventStatusFilter && (sportId || !sportId) && (compId || !compId) && filterEvents();
	$: page = 1;
	$: pageSize = 100;
	$: links = $SportEventStore.count() && paginationBuilder(SportEventStore.getPagination()); //Used for pagination component
	$: loadingFixtures = {};
	$: numberOfPages =
		($SportEventStore.count() && SportEventStore.getPagination() && SportEventStore.getPagination().total_pages) ?? 0;

	let contextMenuOpen;
	let confirmRefundOpen;
	let showBetsForMarket = false;
	let marketToShowBets = undefined;
	let eventToShowBets = undefined;
	let showCreateSportEvent = false;

	function checkUrl() {
		const urlParams = new URLSearchParams(window.location.search);
		showCreateSportEvent = urlParams.get('showCreateSportEvent') === 'true';
	}

	function getSportEvents(showLoading = false) {
		if (showLoading) {
			loading = true;
			window.scrollTo(0, 0);
		}
		return SportEventStore.getSportEvents(
			{
				sportId: sportId,
				compId: compId,
				status: eventStatusFilter
			},
			true,
			{ size: pageSize, number: page },
			true,
			false,
			true
		).finally(() => (loading = false));
	}

	function filterEvents() {
		let filtered = $SportEventStore.whereIn('status.id', eventStatusFilter).sortBy('start_time');
		filtered = sportId ? filtered.where('sport.id', parseInt(sportId)) : filtered;
		return compId ? filtered.where('competition.id', parseInt(compId)) : filtered;
	}

	function getSportcastFixturesForComp(compId) {
		loadingFixtures[compId] = true;
		SportcastFixtureStore.getSportcastFixturesForComp(compId).finally(() => {
			loadingFixtures[compId] = false;
		});
	}

	function changePage(e) {
		if (e) {
			page = e.detail.page;
			getSportEvents();
		}
	}

	ClassificationStore.getClassifications(BetCategory.Sport);

	const handleKeydown = (e) => {
		if (e.key === 'Escape' && confirmRefundOpen !== undefined) {
			confirmRefundOpen = undefined;
		}
		if (e.key === 'Escape' && contextMenuOpen !== undefined) {
			contextMenuOpen = undefined;
		}
	};

	const handleClickAway = (e) => {
		if (confirmRefundOpen !== undefined && !e.target.closest('.refund_window')) {
			confirmRefundOpen = undefined;
		}
		if (
			contextMenuOpen !== undefined &&
			!e.target.closest('.market_context_menu') &&
			!e.target.closest('.market_context_menu_button')
		) {
			contextMenuOpen = undefined;
		}
	};

	afterUpdate(() => {
		checkUrl();
	});

	onMount(() => {
		Sockets.subscribeToSport();
	});

	onDestroy(() => {
		Sockets.unsubscribeFromSport();
	});

	$: compHeadings = events && getCompHeadings();

	function getCompHeadings() {
		const compHeaders = [];
		events
			.sortBy('start_time')
			.when(!compId, (items) => items.take(pageSize))
			.each((item) => {
				let exists = false;
				for (const header of compHeaders) {
					if (header.competition.id == item.competition.id) {
						exists = true;
					}
				}
				if (!exists) {
					compHeaders.push({
						competition: {
							id: item.competition.id,
							name: item.competition.name,
							sportcast_competition_id: item.competition.sportcast_competition_id
						},
						sport: {
							name: item.sport.name
						}
					});
				}
			});
		return compHeaders;
	}

	let currentTabIndex = 1;

	function getTabIndexStart(increment = true, incrementValue = 9) {
		const returnIndex = currentTabIndex;
		if (increment) {
			currentTabIndex = currentTabIndex + incrementValue;
		}
		return returnIndex;
	}
</script>

<Helmet title="Sport" />

<Polling pollFunction={getSportEvents} frequency={60} />

<svelte:window on:keydown={handleKeydown} on:mousedown={handleClickAway} />

{#if showManualResult}
	<ManualResult
		market_id={resultMarket.id}
		eventName={resultEvent.name}
		bind:open={showManualResult}
		on:resulted={() => MarketStore.getMarketWithSelections(resultMarket.id)}
	/>
{/if}

{#if showCreateSportEvent}
	<CreateSportEvent {sport} bind:open={showCreateSportEvent} />
{/if}

<div class="py-2 md:p-2 pt-0 md:mx-2 bg-white rounded">
	<!-- HEADER -->
	<div class="flex flex-col md:flex-row mx-auto max-w-custom">
		<LeftMenu />
		<div class="w-full">
			<!-- EVENT LIST -->
			<div>
				{#if showStatusFilters}
					<div class="my-2 text-center">
						<div class="btn-group">
							<button
								class="btn {arrayEquals(eventStatusFilter, [EventStatus.Open])
									? 'btn-primary-action'
									: 'btn-muted text-dark'}"
								on:click={() => {
									eventStatusFilter = [EventStatus.Open];
									getSportEvents(false);
								}}
							>
								Prematch Only
							</button>
							<button
								class="btn {arrayEquals(eventStatusFilter, [EventStatus.InPlay])
									? 'btn-primary-action'
									: 'btn-muted text-dark'}"
								on:click={() => {
									eventStatusFilter = [EventStatus.InPlay];
									getSportEvents(false);
								}}
							>
								Live Only
							</button>
							<button
								class="btn {arrayEquals(eventStatusFilter, [EventStatus.Open, EventStatus.InPlay])
									? 'btn-primary-action'
									: 'btn-muted text-dark'}"
								on:click={() => {
									eventStatusFilter = [EventStatus.Open, EventStatus.InPlay];
									getSportEvents(false);
								}}
							>
								Prematch & Live
							</button>
						</div>
					</div>
				{/if}
				{#if !compId && !sportId}
					<div class="py-2 font-semibold text-center uppercase">Upcoming</div>
				{/if}
				<div class="md:px-2">
					{#if !loading}
						{#if events.count()}
							{#if numberOfPages > 1 && compId}
								<Pagination {links} on:click={changePage} className="my-3" />
							{/if}
							<div class="flex flex-col space-y-1">
								{#each compHeadings as compHeading (compHeading.competition.id)}
									<div class="flex items-center bg-black pr-0.5 w-full">
										<div class="font-semibold text-white inline-block px-2 py-1.5 cursor-pointer">
											{compHeading.sport.name} - {compHeading.competition.name}
										</div>
										{#if compHeading.competition.sportcast_competition_id}
											<LoadingButton
												loading={loadingFixtures[compHeading.competition.id]}
												className="ml-2 btn bg-blue-200 text-xs font-semibold py-1 whitespace-nowrap text-gray-800 hover:text-blue-900 hover:bg-blue-300"
												on:click={() => getSportcastFixturesForComp(compHeading.competition.id)}
												>Get SGM Events
											</LoadingButton>
										{/if}
									</div>
									{#each events
										.sortBy('start_time')
										.when(!compId, (items) => items.take(pageSize))
										.all() as event (event.id)}
										{#if event.competition.id === compHeading.competition.id}
											<EventWithKeyMarketsDashboard
												{event}
												tabIndexValue={getTabIndexStart()}
												bind:contextMenuOpen
												bind:resultMarket
												bind:resultEvent
												bind:showManualResult
												bind:confirmRefundOpen
												bind:showBetsForMarket
												bind:marketToShowBets
												bind:eventToShowBets
											/>
										{/if}
									{/each}
								{/each}
							</div>
							{#if numberOfPages > 1 && compId}
								<Pagination {links} on:click={changePage} className="my-3" />
							{/if}
						{:else}
							<div class="py-4 mt-4 font-semibold text-center border">No events available</div>
						{/if}
					{:else}
						<Loading />
					{/if}
				</div>
			</div>
		</div>
	</div>
</div>
